import { useApi } from 'api/api-context';
import { GetCurrentUserResponse } from 'api/actions/get-current-user/get-current-user-response';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import panic from 'panic';
import PinCodePresenter from 'pages/house/pincode/PinCodePresenter';
import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  HOUSE_PAGE_PATH,
  TEMPORARY_PIN_CODE_PAGE_PATH,
  USER_TEMPORARY_PIN_CODE_PAGE_PATH,
  USERS_PAGE_PATH,
} from 'routes/paths';

/**
 * API-backed adapter for the permanent PIN code page.
 */
export default function PinCodeApiAdapter() {
  const { _t } = useLang();
  const { getAction, buildingId } = useApi();
  const { userId } = useParams<{ userId?: string }>();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pinCode, setPinCode] = useState<string | null>(null);
  const [remainingChanges, setRemainingChanges] = useState<number | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [pinCodeEnabled, setPinCodeEnabled] = useState<boolean | null>(null);

  const familyMemberId = userId ? Number(userId) : null;
  const isFamilyMemberPin = familyMemberId !== null && !Number.isNaN(familyMemberId);

  /**
   * Loads the current building feature configuration.
   */
  const loadPinCodeAvailability = () => {
    return getAction('GetCurrentUser')({}).then((response) => {
      const buildings = response.buildings || [];
      const currentBuilding = buildings.find(
        (building): building is NonNullable<GetCurrentUserResponse['buildings']>[0] => building.id === buildingId
      );

      return currentBuilding?.showPinCode === true;
    });
  };

  /**
   * Loads the current PIN state from the active backend endpoint.
   */
  const loadPinCode = () => {
    setLoading(true);

    loadPinCodeAvailability()
      .then((enabled) => {
        setPinCodeEnabled(enabled);

        if (!enabled) {
          setPinCode(null);
          setRemainingChanges(null);
          return null;
        }

        const request = isFamilyMemberPin
          ? getAction('GetFamilyMemberPin')({ parameters: { userId: familyMemberId } })
          : getAction('GetUserPin')({});

        return request.then((response) => {
          setPinCode(response.pinCode ?? null);
          setRemainingChanges(response.remainingChanges ?? null);
        });
      })
      .catch(panic)
      .finally(() => setLoading(false));
  };

  /**
   * Requests a new PIN code from the active backend endpoint.
   */
  const handleGeneratePin = () => {
    if (remainingChanges === 0) return;

    setGenerating(true);

    const request = isFamilyMemberPin
      ? getAction('GenerateFamilyMemberPin')({ parameters: { userId: familyMemberId } })
      : getAction('GenerateUserPin')({});

    request
      .then((response) => {
        setPinCode(response.pinCode ?? null);
        setRemainingChanges(response.remainingChanges ?? null);
      })
      .catch(panic)
      .finally(() => setGenerating(false));
  };

  useEffect(() => {
    loadPinCode();
  }, [buildingId, familyMemberId, getAction, isFamilyMemberPin]);

  if (pinCodeEnabled === false) {
    return <Navigate to={isFamilyMemberPin ? USERS_PAGE_PATH.original : HOUSE_PAGE_PATH.original} replace />;
  }

  return (
    <SimpleLayout>
      <PinCodePresenter
        backPath={isFamilyMemberPin ? USERS_PAGE_PATH.original : HOUSE_PAGE_PATH.original}
        backLabel={isFamilyMemberPin ? _t('List of users') : _t('Back to my household')}
        loading={loading}
        generating={generating}
        pinCode={pinCode}
        remainingChanges={remainingChanges}
        showPin={showPin}
        temporaryPinCodesPath={
          isFamilyMemberPin
            ? USER_TEMPORARY_PIN_CODE_PAGE_PATH.insert({ userId: familyMemberId })
            : TEMPORARY_PIN_CODE_PAGE_PATH.original
        }
        onGeneratePin={handleGeneratePin}
        onShowPinStart={() => setShowPin(true)}
        onShowPinEnd={() => setShowPin(false)}
        _t={_t}
      />
    </SimpleLayout>
  );
}
